import type { Mark, Node as ProseMirrorNode } from "prosemirror-model";

/**
 * ProseMirror types attribute bags as `Record<string, any>`. Every read of an
 * attribute in this package funnels through these helpers so that the rest of
 * the codebase stays free of unchecked values.
 */
type AttributeHolder = Pick<ProseMirrorNode | Mark, "attrs">;

export function rawAttr(holder: AttributeHolder, name: string): unknown {
  const bag: Record<string, unknown> = holder.attrs;
  return bag[name];
}

export function stringAttr(holder: AttributeHolder, name: string): string | null {
  const value = rawAttr(holder, name);
  return typeof value === "string" ? value : null;
}

export function numberAttr(holder: AttributeHolder, name: string): number | null {
  const value = rawAttr(holder, name);
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

export function booleanAttr(holder: AttributeHolder, name: string): boolean {
  return rawAttr(holder, name) === true;
}

/** Drops null/undefined entries so the result is a valid DOM attribute bag. */
export function domAttrs(
  source: Record<string, string | number | null | undefined>,
): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(source)) {
    if (value === null || value === undefined) continue;
    result[key] = typeof value === "number" ? String(value) : value;
  }
  return result;
}

/** Reads a DOM attribute from an element without leaking `any`. */
export function domAttr(node: HTMLElement | string, name: string): string | null {
  if (typeof node === "string") return null;
  return node.getAttribute(name);
}
